import React from 'react';
import { BsX, BsLink45Deg, BsFileEarmarkArrowDownFill } from 'react-icons/bs';
import { FaUsers, FaCalendarAlt, FaBullhorn, FaExclamationTriangle } from 'react-icons/fa';

interface HelpDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '20px' }}>
    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '8px', borderBottom: '1px solid #eee', paddingBottom: '6px' }}>
      {title}
    </h4>
    <div style={{ fontSize: '13px', color: '#555', lineHeight: '1.6' }}>{children}</div>
  </div>
);

const ActionItem: React.FC<{ icon: React.ReactNode; label: string; description: string; bgColor: string }> = ({ icon, label, description, bgColor }) => (
  <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '10px' }}>
    <span style={{
      fontSize: '12px',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '28px',
      height: '28px',
      borderRadius: '3px',
      border: '1px solid #ccc',
      backgroundColor: bgColor,
    }}>{icon}</span>
    <div>
      <strong>{label}</strong>
      <div style={{ color: '#666', fontSize: '12px' }}>{description}</div>
    </div>
  </div>
);

const DRAWER_WIDTH = 320;

const HelpDrawer: React.FC<HelpDrawerProps> = ({ isOpen, onClose, children }) => {
  return (
    <>
      <div style={{ marginRight: isOpen ? DRAWER_WIDTH : 0, transition: 'margin-right 0.3s ease' }}>
        {children}
      </div>

      <div style={{
        position: 'fixed',
        top: 0,
        right: isOpen ? 0 : -DRAWER_WIDTH,
        width: DRAWER_WIDTH,
        height: '100vh',
        backgroundColor: '#fff',
        borderLeft: '1px solid #e0e0e0',
        boxShadow: '-2px 0 8px rgba(0,0,0,0.08)',
        transition: 'right 0.3s ease',
        zIndex: 1050,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(180deg, #2a2d3a 0%, #3c3f4c 100%)',
          color: '#fff',
        }}>
          <h3 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Ajuda — Bancas</h3>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: '18px', cursor: 'pointer', padding: '0 4px' }}
          >
            <BsX />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px', fontSize: '13px' }}>
          <Section title="Visão Geral">
            <p style={{ margin: '0 0 8px 0' }}>
              Gerencie o ciclo de vida das bancas: criação, composição, convocação, realização e fechamento.
            </p>
          </Section>

          <Section title="Colunas da Tabela">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li><strong>Tipo:</strong> Facial, Virtual ou Recursal.</li>
              <li><strong>Data:</strong> Data e horário agendados.</li>
              <li><strong>Forma:</strong> Presencial ou Remoto.</li>
              <li><strong>Status:</strong> Estado atual no fluxo.</li>
              <li><strong>Ações:</strong> Botões de operação.</li>
            </ul>
          </Section>

          <Section title="Botões de Ação">
            <ActionItem icon={<BsLink45Deg />} label="Sala" description="Link da sala virtual ou local presencial." bgColor="white" />
            <ActionItem icon={<FaCalendarAlt />} label="Data" description="Data e horários de início e fim." bgColor="#ffeb99" />
            <ActionItem icon={<FaUsers />} label="Composição" description="Membros titulares, suplentes, técnicos e secretários." bgColor="#cc99ff" />
            <ActionItem icon={<FaBullhorn />} label="Convocar" description="Envia email de convocação aos membros definidos." bgColor="#ffb366" />
            <ActionItem icon={<FaExclamationTriangle />} label="Emergência" description="Substitui membro e notifica o novo por email." bgColor="#dc3545" />
            <ActionItem icon={<BsFileEarmarkArrowDownFill />} label="ATA" description="Gera a ata com resultados das votações." bgColor="#66cc99" />
          </Section>

          <Section title="Fluxo de Status">
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
              {[
                { status: 'Indefinida', desc: 'Aguardando configuração.' },
                { status: 'Planejada', desc: 'Composição e data definidas.' },
                { status: 'Convocada', desc: 'Emails enviados aos membros.' },
                { status: 'Em andamento', desc: 'Banca em realização.' },
                { status: 'Fechada', desc: 'Banca encerrada por desuso.' },
                { status: 'Finalizada', desc: 'Votações encerradas.' },
                { status: 'Cancelada', desc: 'Banca cancelada.' },
              ].map(({ status, desc }) => (
                <div key={status} style={{ display: 'flex', gap: '8px' }}>
                  <strong style={{ minWidth: '90px' }}>{status}:</strong>
                  <span>{desc}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Dicas">
            <ul style={{ margin: 0, paddingLeft: '18px' }}>
              <li>Use "Ocultar bancas finalizadas" para focar nas ativas.</li>
              <li>Clique nos headers para ordenar a tabela.</li>
              <li>✓ verde = etapa concluída.</li>
              <li>Botão cinza = indisponível para o status atual.</li>
            </ul>
          </Section>
        </div>
      </div>
    </>
  );
};

export default HelpDrawer;
